// /src/utils/smart_alert_button.js

import axios from "axios";
import { getBestLocation } from "./geo_location";
import { getAuth } from "firebase/auth";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const api = (path) => `${BASE}${path.startsWith("/") ? "" : "/"}${path}`;

async function fetchQuota(token) {
  const res = await axios.get(api("/user/billing"), {
    headers: { Authorization: `Bearer ${token}` },
  });

  const smartAlert = res.data?.smart_alert || {};

  const monthlyRemaining =
    typeof smartAlert.monthly_remaining === "number"
      ? Math.max(0, smartAlert.monthly_remaining)
      : Math.max(
          0,
          Number(
            res.data?.monthly_entitlements?.smart_alert_remaining_alerts ?? 0
          ) || 0
        );

  const addonRemaining =
    typeof smartAlert.addon_remaining === "number"
      ? Math.max(0, smartAlert.addon_remaining)
      : Math.max(
          0,
          Number(
            res.data?.addon_entitlements?.smart_alert_remaining_alerts ?? 0
          ) || 0
        );

  const totalAvailable =
    typeof smartAlert.total_available === "number"
      ? Math.max(0, smartAlert.total_available)
      : monthlyRemaining + addonRemaining;

  return {
    monthlyRemaining,
    addonRemaining,
    totalAvailable,
  };
}

async function postSendAlert(token, payload) {
  const res = await axios.post(api("/alerts/send-alert"), payload, {
    headers: { Authorization: `Bearer ${token}` },
  });

  return res.data;
}

function hasUsableCoordinates(location) {
  return (
    location &&
    location.lat !== null &&
    location.lat !== undefined &&
    location.lon !== null &&
    location.lon !== undefined &&
    Number.isFinite(Number(location.lat)) &&
    Number.isFinite(Number(location.lon))
  );
}

async function resolveSmartAlertLocation() {
  const location = await getBestLocation({
    deadlineMs: 3000,
    prefer: "fine",
  });

  if (!hasUsableCoordinates(location)) {
    return null;
  }

  return location;
}

export const validateAndTriggerSmartAlert = async ({
  mode = "test",
  onFire = () => {},
  setStatusText = () => {},
}) => {
  try {
    const user = getAuth().currentUser;

    if (!user) {
      setStatusText("Login Required");
      return null;
    }

    const token = await user.getIdToken();

    setStatusText("Checking Quota…");

    const quota = await fetchQuota(token);

    if (quota.totalAvailable <= 0) {
      setStatusText("Quota Used");
      return null;
    }

    // Always try a fresh/on-demand browser/device location first.
    // geo_location.js then falls back to trusted last-known browser/device
    // location, and only after that to coarse IP when no trusted fix exists.
    setStatusText("Getting Location…");

    const location = await resolveSmartAlertLocation();

    if (!location) {
      setStatusText("Location Failed");
      return null;
    }

    setStatusText("Sending…");

    const sendResult = await postSendAlert(token, {
      location,
      mode,
    });

    setStatusText("Sent!");

    // Pass the authoritative /alerts/send-alert response back to the page.
    // This includes denied_contacts for channels that were already present on
    // the global Smart Alert deny list and therefore skipped before dispatch.
    onFire(sendResult);

    return sendResult;
  } catch (error) {
    console.error("🔥 Smart Alert Failed:", error);

    const status = error?.response?.status;
    const backendMessage = error?.response?.data?.error;
    const errorCode = error?.response?.data?.error_code;

    if (status === 401) {
      setStatusText("Login Required");
      return null;
    }

    if (status === 403) {
      setStatusText("Quota Used");
      return null;
    }

    if (
      status === 400 &&
      errorCode === "smart_alert_contacts_blocked"
    ) {
      setStatusText("All Blocked");
      return null;
    }

    if (status === 400 && backendMessage) {
      setStatusText("Check Settings");
      return null;
    }

    setStatusText("Failed");
    return null;
  }
};
